// The pre-need quote flow: the harness that carries a family from their
// quote into the application. Position lives in the URL — one route per
// station, so the browser's history is the flow's history — capped at
// the server's derived step by step-routes' landing rule; this page owns
// the flow chrome and the transitions, while FormV2 owns the application
// step's screens.
//
// The device's credential is an HttpOnly cookie the after-api gateway
// mints and holds — this page never sees a token. It arrives one of three
// ways: a one-time link (`?t=`), a signed lead handoff priced before the
// quote itself exists (`?ped&sig&exp`), or bare (a returning device whose
// cookie may already grant it). Two axes track where that arrival stands:
// `credential` (do we have a session yet, and if not, why not) and the
// derived pricing DTO — `view` once a real quote loaded, `bootstrapDto`
// when only the signed-handoff preview has arrived. `flags.ts` commits
// once either exists.
//
// The reveal is gated: skeleton chrome renders instantly, the packages
// flag shapes the card skeletons, and the real content fades in only
// once pricing has arrived AND the flags have committed — so what
// appears never collapses or swaps. Flags fail open and stick (flags.ts).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { motion } from "framer-motion";

import Header from "components/Header";
import type { QuoteFlowWidth } from "components/Header/Header";
import { useFacebookPixel } from "components/FacebookPixelProvider";
import { formatPhoneNumber } from "helpers/phone";
import { useUser } from "context/UserContext";
import { NetworkError } from "hooks/usePublicAPI";
import type { AnalyticsProperties } from "analytics/AnalyticsContext";

import AccountStep from "./AccountStep";
import SaveQuoteBar from "./SaveQuoteBar";
import {
  canCreateAccountFrom,
  readBarDismissed,
  saveQuoteBarVisible,
  writeBarDismissed,
} from "./account";
import {
  refusalFromBody,
  type AccessOutcome,
  type Refusal,
} from "./quote-access";
import { useQuoteAccess } from "./useQuoteAccess";
import { grantRedirectOf } from "./session-grant";
import {
  AWAITING_CONFIRMATION_REASON,
  confirmationScreenFor,
  confirmedDestination,
  submissionIdOf,
} from "pages/StartLead/awaitingConfirmation";
import { clearStoredState } from "pages/StartLead/state";
import { submissionStatusClientFor } from "pages/StartLead/submissionStatusClient";
import ApplicationStep, {
  ApplicationLoadingScreen,
  offeredFor,
} from "./ApplicationStep";
import PaymentStep from "./PaymentStep";
import AuthInterstitial, {
  type InterstitialCredential,
} from "./AuthInterstitial";
import {
  PROVENANCE_NONE,
  QuoteFlowAnalyticsProvider,
  useQuoteFlowAnalytics,
  type QuoteFlowProvenance,
  type QuoteFlowStepName,
  type QuoteType,
} from "./analytics";
import { tagQuoteFlowScope } from "./reporting";

import {
  deleteCheckpoint,
  editApplication,
  fetchQuoteBootstrap,
  fetchQuotePresence,
  fetchQuoteView,
  fetchScheduledCall,
  mintQuoteSession,
  putCheckpoint,
  quoteFormClient,
  recordSelection,
  type QuoteBootstrapResponse,
  type QuoteCheckpoint,
  type QuotePresence,
  type QuoteViewResponse,
  type ScheduledCall,
  ensureSignDocuments,
} from "./api";
import { singlePlanTreatment, useQuoteFlags } from "./flags";
import {
  requestedStation,
  serverStation,
  serverStepFor,
  stationPath,
} from "./step-routes";
import { NavCallActions } from "./PhoneBand";
import { SUPPORT_PHONE_HREF, SUPPORT_PHONE_LABEL } from "constants/phones";
import { hiddenBaselineFeatures, visiblePlans } from "./PricingCards";
import { callGateLanding } from "./call-gate-landing";
import {
  PLACEMENT_PENDING,
  placedStation,
  placementReducer,
  screenFor,
  flowBarWidth,
  stationOnScreen,
  type ArrivalScreen,
  type ScreenFacts,
} from "./arrival";
import ArrivalLoadingScreen from "./ArrivalLoadingScreen";
import { OutOfAreaScreen } from "./JunctureScreens";
import { checkpointsOf } from "./checkpoints";
import { checkpointSyncAction } from "./quiz/checkpoint-sync";
import { deriveConclusion, initialQuizScreen, slugFor } from "./quiz/engine";
import { QUIZ_QUESTIONS } from "./quiz/questions";
import { clearQuiz, hasStoredAnswers, loadQuiz } from "./quiz/store";
import QuizStation from "./quiz/QuizStation";
import type { Answers, QuizResolution } from "./quiz/types";
import QuoteScreen, { type ChosenOption } from "./QuoteScreen";
import QuoteScreenSkeleton from "./QuoteScreenSkeleton";
import ScheduleCallModal from "./ScheduleCallModal";
import ScheduleCallStep from "./ScheduleCallStep";
import FinalizeStep from "./FinalizeStep";
import SignStep from "./SignStep";
import SuccessView from "./SuccessView";

// Structurally the same union as analytics.tsx's QuoteFlowStepName —
// aliased rather than duplicated so the two can never drift.
type FlowStep = QuoteFlowStepName;

/** `phase()`'s internal screen keys, translated to the taxonomy's
 * snake_case `screen` property. Keys not listed already match. */
const SCREEN_NAMES: Record<string, string> = {
  "not-found": "not_found",
  "app-loading": "app_loading",
  "schedule-call": "schedule_call",
  "out-of-area": "out_of_area",
};

/** A dead end the arrival machine cannot recover from on its own — and
 * what led there, so the screen that reports it can say why. */
interface FatalState {
  kind: "not_found" | "error";
  /** The request that failed; null when nothing was even asked (no id). */
  operation: string | null;
  /** Its HTTP status; null off the network. */
  status: number | null;
}

function fatalFrom(
  kind: FatalState["kind"],
  operation: string,
  error: unknown,
): FatalState {
  return {
    kind,
    operation,
    status: error instanceof NetworkError ? error.statusCode : null,
  };
}

/**
 * Whether this device holds (or is on its way to holding) the session
 * cookie the flow authorizes every write on. `absent` covers the bare
 * arrival — no credential rode the URL at all, so the state just reflects
 * whatever the existing cookie turns out to be worth.
 */
type CredentialState =
  | { kind: "link_redeeming" }
  | { kind: "bootstrapping" }
  | { kind: "polling" }
  | { kind: "granted" }
  | { kind: "refused"; reason: string; presence: QuotePresence | null }
  | { kind: "exhausted" }
  | { kind: "absent" };

/** The credential as the screen decision reads it: granted, one of its
 * two dead ends, or still on its way. */
function credentialFactOf(
  credential: CredentialState,
): ScreenFacts["credential"] {
  if (credential.kind === "granted") return "granted";
  if (credential.kind === "refused") return "refused";
  if (credential.kind === "exhausted") return "exhausted";
  return "pending";
}

/** The signature poll's budget and backoff (contract §6): 750ms, ×1.5
 * capped at 3s, 60s total. */
const POLL_BUDGET_MS = 60_000;
const POLL_INITIAL_DELAY_MS = 750;
const POLL_BACKOFF_FACTOR = 1.5;
const POLL_MAX_DELAY_MS = 3_000;

const grantClient = submissionStatusClientFor(async () => null);

/** How long the schedule-call station waits for the booked-call read
 * before showing the picker anyway. Long enough for the read's normal
 * answer, short enough that a hung read never holds a family on the
 * loader. */
const CALL_READ_HOLD_MS = 3_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** The tab's name for each station — a name, beside the site's own. */
const STATION_TITLES: Record<FlowStep, string> = {
  quiz: "A few questions",
  "schedule-call": "Schedule a call",
  quote: "Your quote",
  application: "Your application",
  review: "Review your application",
  payment: "Payment method",
  sign: "Sign documents",
  finalize: "Confirm and submit",
  done: "All set",
  account: "Your account",
};

/** The tab's name: the station's, unless a screen standing in for the
 * station names itself. */
function tabTitleFor(
  screen: ArrivalScreen,
  step: FlowStep | null,
): string | null {
  if (screen === "out-of-area") return "What we can do";
  return step === null ? null : STATION_TITLES[step];
}

/** The stored phone ("+14802000000") dressed for copy ("(480) 200-0000"). */
function displayPhone(phone: string | null): string | null {
  if (!phone) return null;
  return formatPhoneNumber(phone) || phone;
}

/** The "Quote prepared …" line's date: the real quote's creation, or the
 * bootstrap preview's own pricing timestamp when there's no quote yet. */
function preparedAtOf(dto: QuoteViewResponse | QuoteBootstrapResponse): string {
  return dto.quote === null ? dto.prepared_at : dto.quote.created_at;
}

function NotFoundView() {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">
        We couldn&rsquo;t find that quote
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        The link may have expired or been mistyped. Reach out and we&rsquo;ll
        send you a fresh one.
      </p>
    </main>
  );
}

/** A read that failed for a reason other than "no such quote" — the
 * gateway was unreachable, answered 5xx, or the browser refused the
 * response. Nothing is known about the quote either way, so the copy says
 * so and offers a reload rather than a false "not found". */
function ErrorView() {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">
        Something went wrong on our end
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        We couldn&rsquo;t load your quote just now. Please try again in a moment
        — if it keeps happening, call us at{" "}
        <a href={SUPPORT_PHONE_HREF} className="font-bold text-soil">
          {SUPPORT_PHONE_LABEL}
        </a>
        .
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-6 inline-flex items-center justify-center rounded-lg border-2 border-soil bg-transparent px-5 py-2.5 text-base font-bold text-soil transition-all hover:bg-soil hover:text-ivory"
      >
        Try again
      </button>
    </main>
  );
}

function PricingUnavailableView() {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">
        We&rsquo;re not quite ready to price this yet
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        We&rsquo;re missing a detail or two we need to quote your plans. A
        pre-need planning specialist will reach out to help.
      </p>
    </main>
  );
}

/** The signature poll spent its whole budget without a grant or a
 * refusal — the handoff itself is fine, something just took too long.
 * "Try again" restarts the same poll rather than reloading the page, so a
 * transient hiccup doesn't cost the family their place in line. */
function StillPreparingView({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">
        We&rsquo;re still preparing your quote
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        This is taking longer than it should. Call us at{" "}
        <a href={SUPPORT_PHONE_HREF} className="font-bold text-soil">
          {SUPPORT_PHONE_LABEL}
        </a>{" "}
        and we&rsquo;ll get you sorted.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 inline-flex items-center justify-center rounded-lg border-2 border-soil bg-transparent px-5 py-2.5 text-base font-bold text-soil transition-all hover:bg-soil hover:text-ivory"
      >
        Try again
      </button>
    </main>
  );
}

/** The URL's one-time arrival credential — read once at mount by the
 * outer component below so it can double as the analytics provider's
 * static `credential` prop. */
interface ArrivalQuery {
  t: string | null;
  ped: string | null;
  sig: string | null;
  exp: string | null;
}

/**
 * Where a visit came from, as the view answered it. A field the server
 * never sent is not the same answer as one it sent as null: an absent
 * field means nothing was said, and reports as unknown, while an
 * explicit null says there was genuinely no link and nobody to name.
 */
function answered(value: string | null | undefined): string | null {
  if (value === undefined) return null;
  return value ?? PROVENANCE_NONE;
}

function provenanceOfView(view: QuoteViewResponse): QuoteFlowProvenance {
  return {
    quoteOrigin: view.quote.origin_kind,
    grantChannel: answered(view.grant_channel),
    grantOrigin: answered(view.grant_origin),
  };
}

/** The bootstrap face has answered and has none of them: there is no
 * quote behind it, and nothing was redeemed to reach it. */
const BOOTSTRAP_PROVENANCE: QuoteFlowProvenance = {
  quoteOrigin: PROVENANCE_NONE,
  grantChannel: PROVENANCE_NONE,
  grantOrigin: PROVENANCE_NONE,
};

interface QuoteFlowContentProps {
  quoteId: string | undefined;
  query: ArrivalQuery;
}

/**
 * Everything the flow actually does — split out from the default export
 * only so `QuoteFlowAnalyticsProvider` can sit above it: the provider is
 * a context this component consumes, and a component can't consume a
 * context it renders itself.
 */
function QuoteFlowContent({ quoteId, query }: QuoteFlowContentProps) {
  const { trackEvent } = useFacebookPixel();
  const {
    track,
    setStep: setAnalyticsStep,
    reportFailure,
    noteProvenance,
  } = useQuoteFlowAnalytics();

  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { pathname, search } = useLocation();
  const { step: stepSegment, screenRef: screenRefSegment } = useParams<{
    step?: string;
    screenRef?: string;
  }>();
  const requested = requestedStation(stepSegment);
  // A screen segment means something under the application; a quiz entry
  // key means something under the quiz.
  const urlScreenRef =
    requested === "application" || requested === "quiz"
      ? (screenRefSegment ?? null)
      : null;

  useEffect(() => {
    if (quoteId) tagQuoteFlowScope(quoteId, "policy");
  }, [quoteId]);

  const [fatal, setFatal] = useState<FatalState | null>(null);
  const [credential, setCredential] = useState<CredentialState>(() => {
    if (query.t) return { kind: "link_redeeming" };
    if (query.ped && query.sig && query.exp) return { kind: "bootstrapping" };
    return { kind: "absent" };
  });
  const { user, loading: userLoading } = useUser();
  const signedIn = user !== null && !userLoading;
  const [barDismissed, setBarDismissed] = useState(() =>
    quoteId ? readBarDismissed(quoteId) : false,
  );
  // Set once the family clicks Continue or Schedule on Tier-0 cards (a
  // refused signature that presence still allows showing) — from then on
  // this browser has asked to act, and every further act goes to the
  // interstitial instead of retrying the cards.
  const [tier0Engaged, setTier0Engaged] = useState(false);

  const [view, setView] = useState<QuoteViewResponse | null>(null);
  const [bootstrapDto, setBootstrapDto] =
    useState<QuoteBootstrapResponse | null>(null);
  // What the quote screen actually renders from: the real view once it
  // exists, the signed-handoff preview otherwise. Never both.
  const pricingDto: QuoteViewResponse | QuoteBootstrapResponse | null =
    view ?? bootstrapDto;

  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  // A Continue pressed on bootstrap cards before the session is granted —
  // held here and replayed the moment the grant lands and the view loads,
  // so the family's pick is never silently dropped.
  const [queuedSelection, setQueuedSelection] = useState<ChosenOption | null>(
    null,
  );
  // The call: the modal overlay, and the booking the band confirms —
  // looked up lazily so the quote render never waits on Cal.com.
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [bookedCall, setBookedCall] = useState<ScheduledCall | null>(null);
  // The quote whose booked-call read has answered (a call, none, or a
  // failure) or whose hold has run out — the schedule-call station waits
  // for it.
  const [callReadFor, setCallReadFor] = useState<string | null>(null);
  // What the application step's content column currently spans — its
  // juncture faces widen to the card grid, and the bar follows.
  const [applicationWidth, setApplicationWidth] =
    useState<QuoteFlowWidth>("form");

  // Whether an arrival's bare URL was already placed: a bare arrival
  // means "take me where I stand" (the server's derived step) exactly
  // once; from then on the bare path is the quote station's own address,
  // or Back from the application could never show the quote screen.
  const arrivalPlacedRef = useRef(false);

  // The checkpoint ledger's current "quiz" resolution as far as this
  // device knows — seeded from every view arrival (applyView), patched
  // from every successful write (syncQuizCheckpoint) without waiting on
  // a fresh view read.
  const quizServerResolutionRef = useRef<QuizResolution | null>(null);

  // Where the flow opens (arrival.ts): pending until the bootstrap's
  // landing or a view places it, and moved by nothing else. The
  // credential's own progress never touches it — a grant is a fact about
  // the session, not about where the family stands.
  const [placement, place] = useReducer(placementReducer, PLACEMENT_PENDING);
  const placed = placedStation(placement, view?.step ?? null);
  // The server's own derivation, for the acts that read how far the
  // application got; the station on screen comes from the placement.
  const derived = view === null ? null : serverStation(view.step);
  // A station the flow is on its way to while the URL catches up: it
  // stands on screen as it is, and releases once the URL names it.
  const [heldStation, setHeldStation] = useState<FlowStep | null>(null);
  const step: FlowStep | null =
    heldStation ??
    stationOnScreen({
      placed,
      requested,
      bareArrival: stepSegment === undefined && !arrivalPlacedRef.current,
      quizProgressStored: quoteId ? hasStoredAnswers(quoteId) : false,
    });

  // The quiz screen this device opens on: the URL's own ask when it named
  // one, else whatever the store resumes to — or the dead end outright
  // when the server's standing checkpoint already says so, even with no
  // local answers on this device. Computed once here so the render below
  // and the URL-correction effect agree.
  const quizScreenRef =
    step === "quiz"
      ? (urlScreenRef ??
        (quoteId
          ? initialQuizScreen({
              storedScreen: loadQuiz(quoteId)?.screen ?? null,
              serverConclusion:
                (view?.checkpoints.find(
                  (checkpoint) => checkpoint.kind === "quiz",
                )?.resolution as QuizResolution | undefined) ?? null,
            })
          : QUIZ_QUESTIONS[0].slug))
      : null;

  // The URL agrees with the landing, by replacement: a capped ask, an
  // unknown segment, and the bare arrival all rewrite to the station
  // actually shown — a correction, never a history entry. Held while
  // nothing has placed the arrival — there is nothing to correct against
  // yet — and the quiz's own forward moves push their own URLs regardless.
  useEffect(() => {
    if (heldStation !== null) {
      if (quoteId && pathname === stationPath(quoteId, heldStation)) {
        setHeldStation(null);
      }
      return;
    }
    if (!quoteId || placed === null || step === null) return;
    arrivalPlacedRef.current = true;
    const canonical = stationPath(
      quoteId,
      step,
      step === "application"
        ? urlScreenRef
        : step === "quiz"
          ? quizScreenRef
          : null,
    );
    if (canonical !== pathname) {
      navigate({ pathname: canonical, search }, { replace: true });
    }
  }, [
    quoteId,
    heldStation,
    placed,
    step,
    urlScreenRef,
    quizScreenRef,
    pathname,
    search,
    navigate,
  ]);

  // A provisional placement the view then moved — the one station change
  // no act of the family's caused — is reported, so it stays rare in fact
  // and not only by design.
  const provisionalStationRef = useRef<FlowStep | null>(null);
  useEffect(() => {
    if (placement.kind === "provisional") {
      provisionalStationRef.current = placement.station;
      return;
    }
    if (placement.kind !== "settled" || placed === null) return;
    const from = provisionalStationRef.current;
    provisionalStationRef.current = null;
    if (from !== null && from !== placed) {
      track("quote_flow_placement_moved", { from, to: placed });
    }
  }, [placement, placed, track]);

  // Back/Forward re-derives against fresh truth: the server's step may
  // have moved since the entry being revisited was pushed, and the
  // landing rule must cap against what stands NOW, not a memoized view.
  const hasViewForPop = view !== null;
  useEffect(() => {
    if (navigationType !== "POP" || !quoteId || !hasViewForPop) return;
    let cancelled = false;
    fetchQuoteView(quoteId)
      .then((loaded) => {
        if (!cancelled) applyView(loaded);
      })
      .catch(() => {
        // The view on hand stands; the next read corrects.
      });
    return () => {
      cancelled = true;
    };
    // Keyed on pathname so every pop re-reads, not just the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationType, pathname, quoteId, hasViewForPop]);

  // The flow's own moves push history entries — that is the whole point.
  const goToStep = useCallback(
    (
      next: FlowStep,
      options?: { screenRef?: string | null; replace?: boolean },
    ) => {
      if (!quoteId) return;
      // The signed handoff's query is the shareable link; it rides along.
      return navigate(
        {
          pathname: stationPath(quoteId, next, options?.screenRef ?? null),
          search,
        },
        { replace: options?.replace ?? false },
      );
    },
    [quoteId, navigate, search],
  );

  // The analytics scope learns the step ahead of the passive effects that
  // report screens in the same commit — hence layout, not passive.
  useLayoutEffect(() => {
    setAnalyticsStep(step);
  }, [step, setAnalyticsStep]);

  // The quiz's local memory is done its job once the flow itself is —
  // nothing left to resume or re-sync.
  useEffect(() => {
    if (step === "done" && quoteId) clearQuiz(quoteId);
  }, [step, quoteId]);

  // Whether pricing has settled one way or the other — a real view, a
  // bootstrap preview, or a dead end that will never produce either. The
  // flags commit here instead of on the view alone, or the skeleton would
  // hold through the whole signature poll.
  const pricingSettled =
    pricingDto !== null ||
    fatal !== null ||
    credential.kind === "exhausted" ||
    credential.kind === "refused";

  // What kind of quote is on screen, known once one has actually come
  // back: the view says so itself; the signed-handoff preview is a
  // pre-need policy quote by construction (the lead form is pre-need
  // only). Null until then — a dead end never learns a kind.
  const quoteSubjectType: QuoteType | null =
    view?.quote.subject_type ?? (bootstrapDto ? "policy" : null);

  const { skeletonPlanSlugs, skeletonSinglePlan, flags } = useQuoteFlags({
    urlQuoteId: quoteId ?? null,
    // Only a real quote's origin describes the group; the bootstrap face
    // has no quote to describe.
    quoteOrigin: view?.quote.origin_kind ?? null,
    canonicalQuoteId: view?.quote.id ?? null,
    viewSettled: pricingSettled,
    subjectType: quoteSubjectType,
  });

  // Invalidates any in-flight signature poll — bumped when the parallel
  // cookie-view read wins the race, when the arrival effect tears down,
  // and by "Try again" starting a fresh attempt.
  const attemptGenerationRef = useRef(0);
  // Which quoteId the handoff arrival has already started for — guards the
  // bootstrap read against StrictMode's dev-only double-invoke, which would
  // otherwise fetch /bootstrap twice per landing. Keyed on quoteId, not a
  // bare boolean, so a genuine quoteId change still starts fresh.
  const startedForRef = useRef<string | null>(null);
  // A persistent (not per-invocation) cancellation flag: StrictMode runs
  // this effect's cleanup between its two invocations, and that cleanup
  // must NOT be allowed to cancel the one real chain the first invocation
  // started — only an actual unmount should. Reset to false at the top of
  // every invocation; the invocation still standing when the component
  // really unmounts is the one whose cleanup sticks.
  const cancelledRef = useRef(false);

  // Every view arrival goes through here, so the analytics scope learns
  // where the visit came from before the render this schedules. That
  // render can mount a child which reports its own arrival, and the
  // application step does exactly that when it lands on the options
  // face; a child's effects run before this component's.
  const applyView = (loaded: QuoteViewResponse) => {
    const checkpoints = checkpointsOf(loaded);
    noteProvenance(provenanceOfView(loaded));
    quizServerResolutionRef.current =
      (checkpoints.find((checkpoint) => checkpoint.kind === "quiz")
        ?.resolution as QuizResolution | undefined) ?? null;
    setView({ ...loaded, checkpoints });
    place({ type: "view" });
  };

  // The step needs no seeding here: it derives from the view reactively,
  // through the landing rule above.
  const finishWithView = (loaded: QuoteViewResponse) => {
    applyView(loaded);
  };

  // A forward move the server just accepted: re-read the view so the
  // landing rule caps against the step the move earned, then go there.
  // When the read fails the milestone still stands — the view on hand is
  // patched to it and the next read reconciles.
  const advanceWithFreshView = async (earned: FlowStep) => {
    if (!quoteId) return;
    try {
      const loaded = await fetchQuoteView(quoteId);
      applyView(loaded);
      goToStep(serverStation(loaded.step));
    } catch {
      setView((current) =>
        current ? { ...current, step: serverStepFor(earned) } : current,
      );
      goToStep(earned);
    }
  };

  /** A mint that answered "awaiting confirmation": the visitor proves who
   * they are on the sign-in screen, which resumes onto this quote. When no
   * grant can be minted, the call-us interstitial stands in. */
  const sendToConfirmation = async (
    submissionId: string | null,
    stale: () => boolean,
  ) => {
    const target = await confirmationScreenFor(
      submissionId,
      grantClient,
      "planning",
    );
    if (stale()) return;
    if (target.kind === "login") {
      navigate(target.path, { replace: true, state: target.state });
      return;
    }
    if (target.kind === "confirmed") {
      clearStoredState();
      navigate(confirmedDestination(target.next, target.submissionId), {
        replace: true,
      });
      return;
    }
    setCredential({
      kind: "refused",
      reason: AWAITING_CONFIRMATION_REASON,
      presence: null,
    });
  };

  /** Applies a refusal to the credential, or sends a lead still awaiting
   * confirmation to the sign-in screen. */
  const refuse = (refusal: Refusal, stale: () => boolean) => {
    if (refusal.reason === AWAITING_CONFIRMATION_REASON) {
      void sendToConfirmation(submissionIdOf(refusal.body), stale);
      return;
    }
    setCredential({
      kind: "refused",
      reason: refusal.reason,
      presence: refusal.presence,
    });
  };

  /** The signature credential's poll: mint attempts on a 750ms→3s
   * backoff, budgeted at 60s total, while the server keeps saying 404
   * (not minted yet). A 200 grants and loads the view; a 409 is a real
   * refusal — a signature that can no longer mint at all. */
  const pollForGrant = (
    id: string,
    handoff: { ped: string; sig: string; exp: string },
    generation: number,
  ) => {
    const startedAt = Date.now();
    let delay = POLL_INITIAL_DELAY_MS;
    // Stale either because a fresher attempt superseded this one
    // (the parallel cookie-read winning, or "Try again") or because the
    // page has actually gone away — cancelledRef only ever reads true for
    // a real unmount (StrictMode's throwaway invocation resets it back).
    const stale = () =>
      attemptGenerationRef.current !== generation || cancelledRef.current;

    const attempt = async () => {
      await sleep(delay);
      if (stale()) return;
      try {
        const grant = await mintQuoteSession(id, {
          kind: "signature",
          ...handoff,
        });
        if (stale()) return;
        const redirect = grantRedirectOf(grant);
        if (redirect) {
          navigate(redirect, { replace: true });
          return;
        }
        setCredential({ kind: "granted" });
        const loaded = await fetchQuoteView(id);
        if (stale()) return;
        finishWithView(loaded);
      } catch (error) {
        if (stale()) return;
        if (error instanceof NetworkError && error.statusCode === 409) {
          refuse(refusalFromBody(error.responseData), stale);
          return;
        }
        if (Date.now() - startedAt >= POLL_BUDGET_MS) {
          setCredential({ kind: "exhausted" });
          // One report per poll, at the moment it gives up — not per
          // retry, or a normal string of "not minted yet" 404s would
          // spam Sentry with noise nobody can act on.
          reportFailure("mint_session_poll", error);
          return;
        }
        delay = Math.min(delay * POLL_BACKOFF_FACTOR, POLL_MAX_DELAY_MS);
        void attempt();
      }
    };

    void attempt();
  };

  /** "Try again" on the exhausted face — a fresh poll, its own budget. */
  const restartPoll = () => {
    if (!quoteId || !query.ped || !query.sig || !query.exp) return;
    const generation = ++attemptGenerationRef.current;
    setCredential({ kind: "polling" });
    pollForGrant(
      quoteId,
      { ped: query.ped, sig: query.sig, exp: query.exp },
      generation,
    );
  };

  const handoffArrival = Boolean(
    !query.t && query.ped && query.sig && query.exp,
  );

  const applyAccessOutcome = (outcome: AccessOutcome<QuoteViewResponse>) => {
    if (outcome.kind === "granted") {
      setCredential({ kind: "granted" });
      finishWithView(outcome.view);
    } else if (outcome.kind === "refused") {
      refuse(outcome, () => cancelledRef.current);
    } else {
      setFatal({
        kind: outcome.kind,
        operation: outcome.operation,
        status: outcome.status,
      });
    }
  };

  const { exchangePending, reentryRefused, signedInAfterReentry } =
    useQuoteAccess({
      quoteId,
      subject: "policy",
      linkToken: query.t,
      openOnArrival: !handoffArrival,
      fetchView: fetchQuoteView,
      fetchPresence: fetchQuotePresence,
      refusalReason: credential.kind === "refused" ? credential.reason : null,
      onOutcome: applyAccessOutcome,
      onExchanged: (loaded) =>
        applyAccessOutcome({ kind: "granted", view: loaded }),
    });

  // The signed handoff's arrival (contract §6), chosen once at mount from
  // the query captured above. A link or a bare arrival is the shared
  // access flow's, started by useQuoteAccess.
  useEffect(() => {
    // Live for this invocation until something says otherwise — see
    // cancelledRef's own comment for why this isn't a per-invocation
    // local instead.
    cancelledRef.current = false;

    // The cleanup every branch below returns: marks whichever chain is
    // still in flight as stale — which the signature poll's own stale()
    // check also reads, so this alone is what actually stops it on a
    // real unmount. Bumping attemptGenerationRef here too would be wrong:
    // StrictMode calls this between its two invocations, and that would
    // invalidate the generation the (still-live) first invocation just
    // captured before the poll ever got to check it. Kept as one function
    // so every branch — including the "already started" guard — wires
    // real-unmount cleanup the same way.
    const stopArrival = () => {
      cancelledRef.current = true;
    };

    if (!quoteId) {
      setFatal({ kind: "not_found", operation: null, status: null });
      return stopArrival;
    }

    if (startedForRef.current === quoteId) {
      // StrictMode's second pass over the same arrival (dev only) — the
      // first invocation already fired the bootstrap read; firing it again
      // would race a second poll. Nothing new to start, just keep
      // real-unmount cleanup wired to whichever chain is actually running.
      return stopArrival;
    }
    startedForRef.current = quoteId;

    // A signed lead handoff. The bootstrap prices from the signed
    // payload and names the landing, which places the arrival ahead of
    // the grant; the signature poll runs alongside a read that covers
    // the cookie already being valid (a repeat visit on the same link).
    if (!query.t && query.ped && query.sig && query.exp) {
      const handoff = { ped: query.ped, sig: query.sig, exp: query.exp };
      const generation = ++attemptGenerationRef.current;

      fetchQuoteBootstrap(quoteId, handoff)
        .then((dto) => {
          if (
            cancelledRef.current ||
            attemptGenerationRef.current !== generation
          ) {
            return;
          }
          noteProvenance(BOOTSTRAP_PROVENANCE);
          setBootstrapDto(dto);
          place({ type: "bootstrap", landing: dto.landing });
          setCredential({ kind: "polling" });
          pollForGrant(quoteId, handoff, generation);
        })
        .catch((error) => {
          if (cancelledRef.current) return;
          if (error instanceof NetworkError && error.statusCode === 404) {
            setFatal(fatalFrom("not_found", "fetch_bootstrap", error));
            return;
          }
          reportFailure("fetch_bootstrap", error);
          setFatal(fatalFrom("error", "fetch_bootstrap", error));
        });

      fetchQuoteView(quoteId)
        .then((loaded) => {
          if (cancelledRef.current) return;
          // The cookie already grants access — the bootstrap/poll dance
          // is moot, and must not be allowed to later overwrite this.
          attemptGenerationRef.current += 1;
          setCredential({ kind: "granted" });
          finishWithView(loaded);
        })
        .catch((error) => {
          if (cancelledRef.current) return;
          // A 404 here is expected on a first arrival — the bootstrap/poll
          // path above is what carries it. Anything else is a real read
          // failure this parallel check swallowed silently.
          if (error instanceof NetworkError && error.statusCode === 404) return;
          reportFailure("fetch_view", error);
        });

      return stopArrival;
    }

    return stopArrival;
    // The query was captured once at mount (lazy initializer) and never
    // changes; quoteId is a route param stable for the page's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  // A refusal (or an exhausted poll) invalidates a schedule modal that
  // was already open — there is no session to load slots against, and
  // the interstitial (or the exhausted face) is about to take over.
  useEffect(() => {
    if (credential.kind === "refused" || credential.kind === "exhausted") {
      setCallModalOpen(false);
    }
  }, [credential.kind]);

  // A Continue queued while the session was still pending, now stranded
  // by a refusal or an exhausted poll — nothing to replay it against, so
  // the busy button stands down rather than spinning forever.
  useEffect(() => {
    if (
      queuedSelection &&
      (credential.kind === "refused" || credential.kind === "exhausted")
    ) {
      setQueuedSelection(null);
      setContinuing(false);
    }
  }, [credential.kind, queuedSelection]);

  // The queued Continue's replay: the moment the grant lands AND the view
  // is in hand, fire the recorded pick and proceed exactly as a granted
  // Continue would have.
  useEffect(() => {
    if (
      !quoteId ||
      !queuedSelection ||
      credential.kind !== "granted" ||
      !view
    ) {
      return;
    }
    let cancelled = false;
    const selection = queuedSelection;
    setQueuedSelection(null);
    recordSelection(quoteId, selection)
      .then((recorded) => {
        if (cancelled) return;
        setView((current) =>
          current
            ? {
                ...current,
                current_selection: recorded.selection,
                step: recorded.step,
              }
            : current,
        );
        track("quote_flow_selection_confirmed", {
          plan_slug: selection.plan_slug,
          option_slug: selection.option_slug,
          source: "quote_screen",
          queued: true,
        });
        goToStep("application");
      })
      .catch((error) => {
        if (!cancelled) {
          reportFailure("record_selection", error);
          setContinueError(
            "We couldn't save that selection — give it another try in a moment.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setContinuing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    quoteId,
    queuedSelection,
    credential.kind,
    view,
    track,
    reportFailure,
    goToStep,
  ]);

  // The booked-call read arrives after the view, quietly — a failure just
  // leaves the band and the station on their not-booked face, but is
  // still worth an engineer's eyes if the read is actually broken rather
  // than merely empty. A call that lands after the hold still switches
  // the station to its booked face.
  const hasView = view !== null;
  useEffect(() => {
    if (!quoteId || !hasView) return;
    let cancelled = false;
    const hold = setTimeout(() => setCallReadFor(quoteId), CALL_READ_HOLD_MS);
    fetchScheduledCall(quoteId)
      .then(({ call }) => {
        if (cancelled) return;
        if (call) setBookedCall(call);
        setCallReadFor(quoteId);
      })
      .catch((error) => {
        if (cancelled) return;
        reportFailure("fetch_scheduled_call", error);
        setCallReadFor(quoteId);
      });
    return () => {
      cancelled = true;
      clearTimeout(hold);
    };
  }, [quoteId, hasView, reportFailure]);

  // A quiz checkpoint write pending because the device didn't hold a
  // granted session yet when it was decided — the signed-handoff arrival
  // renders the quiz before its own grant has landed. Only the latest
  // matters: a fresh decision replaces whatever was queued.
  const pendingQuizAnswersRef = useRef<{
    answers: Answers;
    advance: boolean;
  } | null>(null);
  // Guards a write's own patch of `view`/`quizServerResolutionRef` against
  // a newer write's response landing out of order.
  const quizSyncGenerationRef = useRef(0);

  const patchAfterQuizSync = (
    checkpoint: QuoteCheckpoint | null,
    step: string,
  ) => {
    setView((current) => {
      if (!current) return current;
      const others = current.checkpoints.filter((c) => c.kind !== "quiz");
      return {
        ...current,
        checkpoints: checkpoint ? [...others, checkpoint] : others,
        step,
      };
    });
  };

  /**
   * The quiz checkpoint's diff/sync: compares what these answers now
   * derive against what the server last confirmed (checkpoint-sync.ts),
   * and writes only on a real difference — a PUT whose resolution would
   * deep-equal the standing row is already a no-op, decided before any
   * request goes out. Queues (replacing any earlier queue) rather than
   * writing while this device has no granted session yet.
   */
  const syncQuizCheckpoint = async (answers: Answers, advance: boolean) => {
    if (!quoteId) return;
    const derived = deriveConclusion(answers);
    const action = checkpointSyncAction(
      quizServerResolutionRef.current,
      derived,
    );
    if (action.kind === "none") {
      pendingQuizAnswersRef.current = null;
      // Nothing to write, but a quiz answered through to its end still
      // has to move on: the server's standing step already reflects it.
      if (advance && view && serverStation(view.step) !== "quiz") {
        goToStep(serverStation(view.step));
      }
      return;
    }
    if (credential.kind !== "granted" || view === null) {
      pendingQuizAnswersRef.current = { answers, advance };
      return;
    }
    pendingQuizAnswersRef.current = null;
    const generation = ++quizSyncGenerationRef.current;
    const previousResolution = quizServerResolutionRef.current;
    try {
      if (action.kind === "put") {
        const { checkpoint, step } = await putCheckpoint(
          quoteId,
          "quiz",
          action.resolution,
        );
        if (quizSyncGenerationRef.current !== generation) return;
        quizServerResolutionRef.current = action.resolution;
        patchAfterQuizSync(checkpoint, step);
        track("quote_flow_quiz_concluded", {
          conclusion: action.resolution.conclusion,
          preplan_timing:
            action.resolution.conclusion === "completed"
              ? action.resolution.preplan_timing
              : null,
        });
        if (action.resolution.conclusion === "completed") {
          // Mirrors after-www's build_a_quote_complete — flattened to
          // scalar properties (AnalyticsProperties carries no objects),
          // rather than the single `user_answers` blob after-www fires.
          track("build_a_quote_complete", {
            total_questions: QUIZ_QUESTIONS.length,
            who: typeof answers.who === "string" ? answers.who : null,
            goals: Array.isArray(answers.goals) ? answers.goals : null,
            hospice:
              typeof answers.hospice === "string" ? answers.hospice : null,
            travel: typeof answers.travel === "string" ? answers.travel : null,
            home: typeof answers.home === "string" ? answers.home : null,
            preplan_timing: action.resolution.preplan_timing,
          });
        }
        if (advance && serverStation(step) !== "quiz") {
          goToStep(serverStation(step));
        }
      } else {
        const { step } = await deleteCheckpoint(quoteId, "quiz");
        if (quizSyncGenerationRef.current !== generation) return;
        quizServerResolutionRef.current = null;
        patchAfterQuizSync(null, step);
        if (previousResolution !== null) track("quote_flow_quiz_withdrawn", {});
      }
    } catch (error) {
      reportFailure("quiz_checkpoint_sync", error);
    }
  };

  // The queued write's replay — the moment a granted session AND the
  // view both stand, fire whatever the quiz decided while neither did.
  useEffect(() => {
    if (credential.kind !== "granted" || view === null) return;
    if (pendingQuizAnswersRef.current === null) return;
    const pending = pendingQuizAnswersRef.current;
    void syncQuizCheckpoint(pending.answers, pending.advance);
    // syncQuizCheckpoint reads its own fresh closures each render; only
    // the grant/view transition should re-arm this replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credential.kind, view]);

  /** The call-gate checkpoint: booked or deferred, written the moment the
   * family acts on the schedule-call station, then forward to wherever
   * the server now derives — always the quote screen from here, since a
   * call-gate outcome is the last thing standing in its way. */
  const writeCallGate = async (
    outcome: "booked" | "deferred",
    destination: "derived" | "application" = "derived",
  ) => {
    if (!quoteId) return;
    track(
      outcome === "booked"
        ? "quote_flow_call_gate_booked"
        : "quote_flow_call_gate_deferred",
      {},
    );
    let serverStep: string | null = null;
    try {
      ({ step: serverStep } = await putCheckpoint(quoteId, "call_gate", {
        outcome,
      }));
    } catch (error) {
      reportFailure("call_gate_checkpoint", error);
      // The family's own click already reflects their intent; fall back
      // to where they were headed rather than strand them on a scheduler
      // with nothing left for them to do.
    }
    const landing = callGateLanding(destination, serverStep);
    const { viewStep } = landing;
    if (viewStep !== null) {
      setView((current) =>
        current ? { ...current, step: viewStep } : current,
      );
    }
    if (landing.historyBase === null) {
      goToStep(landing.route);
      return;
    }
    // The view has moved past the schedule call but the URL has not yet:
    // the screen is held on the destination until the URL arrives, and
    // the URL write-back stands down, or the quote screen would flash and
    // its address overwrite the push.
    setHeldStation(landing.route);
    try {
      await goToStep(landing.historyBase, { replace: true });
      await goToStep(landing.route);
    } catch (error) {
      setHeldStation(null);
      throw error;
    }
  };

  /** A checkpoint the flow wrote outside the step derivation: the view's
   * ledger takes the new current row, and the step stays where it is. */
  const recordCheckpoint = (checkpoint: QuoteCheckpoint) => {
    setView((current) =>
      current
        ? {
            ...current,
            checkpoints: [
              ...checkpointsOf(current).filter(
                (row) => row.kind !== checkpoint.kind,
              ),
              checkpoint,
            ],
          }
        : current,
    );
  };

  // Referentially stable across renders — a new client identity would
  // restart the form walk. Keyed on the form REF, never the view object:
  // the view re-reads on every Back/Forward pop, and a client reborn per
  // read would restart the walk mid-pop.
  const applicationFormRef = view?.application_form_ref ?? null;
  const formClient = useMemo(
    () =>
      quoteId && applicationFormRef
        ? quoteFormClient({ quoteId, formRef: applicationFormRef })
        : null,
    [quoteId, applicationFormRef],
  );

  // Scheduling a call opens the application without a selection: the walk
  // asks the union of the health questions, so the family gets as far as
  // they can and the package choice catches up — on their own later, or
  // on the call. The server still derives the step from the ledger, so a
  // reload before any selection lands back on the quote screen; the
  // booked modal's Continue is the way back in.
  const enterApplication = () => {
    setCallModalOpen(false);
    goToStep("application");
  };

  // Tier 0: a refused signature still gets to keep its cards up when the
  // quote is unclaimed and still sitting at the quote screen — everything
  // beyond looking, though, needs the interstitial.
  const tier0Presence =
    credential.kind === "refused" ? credential.presence : null;
  const tier0KeepsCards =
    credential.kind === "refused" &&
    pricingDto !== null &&
    !tier0Engaged &&
    Boolean(tier0Presence?.at_quote_screen) &&
    tier0Presence?.claimed === false;

  const requestSchedule = (source: "nav" | "phone_band") => {
    if (credential.kind === "refused") {
      setTier0Engaged(true);
      return;
    }
    track("quote_flow_schedule_opened", { source });
    setCallModalOpen(true);
  };

  /** Whether the pick differs from the selection standing on the quote —
   * a same-choice Continue moves forward without writing anything. */
  const selectionUnchanged = (choice: ChosenOption): boolean => {
    const current = view?.current_selection ?? null;
    if (!current || !view || view.pricing.status !== "ok") return false;
    if (current.plan_slug !== choice.plan_slug) return false;
    return offeredFor(current, view.pricing.plans)?.slug === choice.option_slug;
  };

  const requestContinue = async (selection: ChosenOption) => {
    if (!quoteId) return;
    if (credential.kind === "refused") {
      setTier0Engaged(true);
      return;
    }
    if (credential.kind !== "granted") {
      // No session to write against yet — hold the pick and show busy;
      // the queued-replay effect above fires it the moment one lands.
      setQueuedSelection(selection);
      setContinuing(true);
      setContinueError(null);
      return;
    }
    // Behind a completed application the quote screen stays live: an
    // unchanged Continue just moves forward, and an actual change IS the
    // edit — the application reopens first, so a selection write never
    // lands against a completed walk (the server refuses that outright).
    const applicationCompleted =
      derived === "payment" || derived === "sign" || derived === "finalize";
    if (applicationCompleted && derived && selectionUnchanged(selection)) {
      goToStep(derived);
      return;
    }
    setContinuing(true);
    setContinueError(null);
    try {
      if (applicationCompleted) {
        await editApplication(quoteId);
      }
      const recorded = await recordSelection(quoteId, selection);
      // The application step reads the selection it opens over — hand it
      // the row just recorded, not the view's pre-Continue snapshot. The
      // step rides along: a reopened application derives "application"
      // again, and the landing rule must know.
      setView((current) =>
        current
          ? {
              ...current,
              current_selection: recorded.selection,
              step: recorded.step,
            }
          : current,
      );
      track("quote_flow_selection_confirmed", {
        plan_slug: selection.plan_slug,
        option_slug: selection.option_slug,
        source: "quote_screen",
        queued: false,
        reopened: applicationCompleted,
      });
      goToStep("application");
    } catch (error) {
      reportFailure("record_selection", error);
      setContinueError(
        "We couldn't save that selection — give it another try in a moment.",
      );
    } finally {
      setContinuing(false);
    }
  };

  // The one decision the render projects (arrival.ts). Every branch
  // below keys off it; the raw-state checks beside a key only narrow
  // types, and can never pick a different screen.
  const screen = screenFor({
    fatal: fatal?.kind ?? null,
    placed,
    station: step,
    requested,
    viewReady: view !== null,
    credential:
      credential.kind === "refused" && exchangePending
        ? "pending"
        : credentialFactOf(credential),
    tier0KeepsCards,
    pricingReady: pricingDto !== null,
    flagsReady: flags !== null,
    callReadSettled: quoteId !== null && callReadFor === quoteId,
    pricingOk:
      pricingDto !== null &&
      pricingDto.pricing.status === "ok" &&
      pricingDto.pricing.plans.length > 0,
    pricingOutOfArea:
      pricingDto !== null && pricingDto.pricing.status === "out_of_area",
    formClientReady: formClient !== null,
  });

  // Every change of the tab's name lands at the top — a Back/Forward pop
  // included, which no button handler covers.
  const tabTitle = tabTitleFor(screen, step);
  useEffect(() => {
    if (tabTitle === null) return;
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = `${tabTitle} · After.com`;
  }, [tabTitle]);

  const phase = (): { key: ArrivalScreen; content: ReactNode } => {
    if (screen === "not-found") {
      return { key: screen, content: <NotFoundView /> };
    }
    if (screen === "error") {
      return { key: screen, content: <ErrorView /> };
    }
    if (screen === "done" && view && quoteId) {
      return {
        key: "done",
        content: (
          <SuccessView
            quoteId={quoteId}
            firstName={view.owner_first_name ?? null}
            offerAccount={
              canCreateAccountFrom(view.quote.origin_kind) &&
              !signedIn &&
              !userLoading
            }
            signedIn={signedIn}
          />
        ),
      };
    }
    if (screen === "finalize" && view && quoteId) {
      return {
        key: "finalize",
        content: (
          <FinalizeStep
            quoteId={quoteId}
            optionSlug={
              view.current_selection && view.pricing.status === "ok"
                ? (offeredFor(view.current_selection, view.pricing.plans)
                    ?.slug ?? null)
                : null
            }
            paymentPeriodOap={
              view.current_selection?.payment_period_oap ?? null
            }
            planName={
              (view.pricing.status === "ok"
                ? view.pricing.plans.find(
                    (plan) => plan.slug === view.current_selection?.plan_slug,
                  )?.name
                : null) ?? null
            }
            onDone={() => void advanceWithFreshView("done")}
            onChangePayment={() => goToStep("payment")}
          />
        ),
      };
    }
    if (screen === "sign" && view && quoteId) {
      return {
        key: "sign",
        content: (
          <SignStep
            quoteId={quoteId}
            bookedCall={bookedCall}
            contactPhone={displayPhone(view.contact_phone)}
            onScheduleCall={() => requestSchedule("phone_band")}
            onDone={() => void advanceWithFreshView("finalize")}
          />
        ),
      };
    }
    if (screen === "payment" && view && quoteId) {
      return {
        key: "payment",
        content: (
          <PaymentStep
            quoteId={quoteId}
            dueTodayCents={view.current_selection?.due_today_cents ?? null}
            onDone={() => {
              // Where next is the server's call: a multi-pay instrument
              // swap stales the packet (re-sign), while single-pay walks
              // straight back to finalize. Documents start building the
              // moment sign is the answer — the intro masks the tail.
              // The payment step stays busy until this settles, so the
              // move rides with the swap: jumping first would hoist the
              // payment screen while it still shows.
              return fetchQuoteView(quoteId)
                .then((loaded) => {
                  applyView(loaded);
                  const next = serverStation(loaded.step);
                  if (next === "sign") {
                    ensureSignDocuments(quoteId).catch((error: unknown) =>
                      reportFailure("ensure_sign_documents", error),
                    );
                  }
                  goToStep(next);
                })
                .catch(() => {
                  ensureSignDocuments(quoteId).catch((error: unknown) =>
                    reportFailure("ensure_sign_documents", error),
                  );
                  // The read failed but the confirm stands — patch the
                  // view so the landing rule lets the move through.
                  setView((current) =>
                    current
                      ? { ...current, step: serverStepFor("sign") }
                      : current,
                  );
                  goToStep("sign");
                });
            }}
          />
        ),
      };
    }
    if (screen === "application" && view && formClient && quoteId) {
      // Completed and not reopened: the application renders as a record
      // (read-only, with the Edit seam) rather than as the walk.
      const applicationCompleted =
        derived === "payment" || derived === "sign" || derived === "finalize";
      return {
        key: "application",
        content: (
          <ApplicationStep
            key={applicationCompleted ? "record" : "walk"}
            quoteId={quoteId}
            formRef={view.application_form_ref}
            client={formClient}
            planningFor={view.planning_for}
            marketSlug={view.market_slug}
            flags={flags}
            applicationCompleted={applicationCompleted}
            pricedPlans={
              view.pricing.status === "ok" ? view.pricing.plans : null
            }
            station={step === "review" ? "review" : "application"}
            urlScreenRef={urlScreenRef}
            onNavigateWithin={(target, options) => {
              const path = stationPath(
                quoteId,
                target.station,
                target.station === "application"
                  ? (target.screenRef ?? null)
                  : null,
              );
              navigate(
                { pathname: path, search },
                options?.replace ? { replace: true } : undefined,
              );
            }}
            onContinueToCurrent={() => {
              if (derived) goToStep(derived);
            }}
            onReopened={() => {
              // The reopen moved the server's step back into the walk —
              // the re-read is what flips this component's mode.
              void fetchQuoteView(quoteId)
                .then((loaded) => applyView(loaded))
                .catch((error: unknown) => reportFailure("fetch_view", error));
            }}
            initialIneligible={view.ineligible}
            initialSelection={view.current_selection}
            initialAcknowledgements={view.acknowledgements}
            bookedCall={bookedCall}
            contactPhone={displayPhone(view.contact_phone)}
            onScheduleCall={() => {
              track("quote_flow_schedule_opened", { source: "phone_band" });
              setCallModalOpen(true);
            }}
            onContentWidthChange={setApplicationWidth}
            onDone={(selection) => {
              // The walk's junctures may have moved the selection since
              // the view loaded — the payment blurb must name the amount
              // actually agreed to.
              if (selection) {
                setView((current) =>
                  current
                    ? { ...current, current_selection: selection }
                    : current,
                );
              }
              void advanceWithFreshView("payment");
            }}
            onExitToQuote={() => goToStep("quote")}
          />
        ),
      };
    }
    if (screen === "exhausted") {
      return {
        key: "exhausted",
        content: <StillPreparingView onRetry={restartPoll} />,
      };
    }
    if (screen === "interstitial" && credential.kind === "refused" && quoteId) {
      return {
        key: "interstitial",
        content: (
          <AuthInterstitial
            quoteId={quoteId}
            reason={credential.reason}
            linkToken={query.t ?? null}
            subject="policy"
            closedAfterProof={reentryRefused}
            onSignedIn={signedInAfterReentry}
          />
        ),
      };
    }
    // The quiz needs only `planning_for`, which the bootstrap preview
    // already carries — a signed arrival the bootstrap placed on the quiz
    // renders it before the grant (and the real quote behind it) exist.
    if (screen === "quiz" && pricingDto !== null && quoteId && quizScreenRef) {
      return {
        key: "quiz",
        content: (
          <QuizStation
            quoteId={quoteId}
            planningFor={pricingDto.planning_for}
            screenRef={quizScreenRef}
            responseAnimationVariant={
              flags?.quiz_response_animation ?? "control"
            }
            onNavigate={(ref) => goToStep("quiz", { screenRef: ref })}
            onAnswersChange={(answers, reachedEnd) =>
              void syncQuizCheckpoint(answers, reachedEnd)
            }
          />
        ),
      };
    }
    if (screen === "schedule-call" && view && quoteId) {
      return {
        key: "schedule-call",
        content: (
          <ScheduleCallStep
            quoteId={quoteId}
            contactPhone={displayPhone(view.contact_phone)}
            credentialPending={credential.kind !== "granted"}
            existingCall={bookedCall}
            onBooked={setBookedCall}
            onBookedContinue={() => void writeCallGate("booked")}
            onDeferred={() => void writeCallGate("deferred")}
            onBack={() =>
              goToStep("quiz", { screenRef: slugFor("preplanTiming") })
            }
            deferralCopyVariant={
              flags?.schedule_call_deferral_copy ?? "control"
            }
            bookingConfirmationVariant={
              flags ? flags.booking_confirmation : null
            }
            marketSlug={view.market_slug}
            checkpoints={checkpointsOf(view)}
            onCheckpointSaved={recordCheckpoint}
            onStartApplication={() => writeCallGate("booked", "application")}
          />
        ),
      };
    }
    if (screen === "account" && quoteId) {
      return { key: "account", content: <AccountStep quoteId={quoteId} /> };
    }
    if (screen === "app-loading") {
      return {
        key: screen,
        content: <ApplicationLoadingScreen />,
      };
    }
    if (screen === "arrival-loading") {
      return { key: screen, content: <ArrivalLoadingScreen /> };
    }
    if (screen === "out-of-area") {
      return {
        key: "out-of-area",
        content: (
          <OutOfAreaScreen
            onPlanningGuideClicked={() =>
              track("quote_flow_screen_action", {
                screen: "out_of_area",
                action: "typeform_clicked",
              })
            }
          />
        ),
      };
    }
    if (screen === "skeleton" || pricingDto === null || flags === null) {
      return {
        key: "skeleton",
        content: (
          <QuoteScreenSkeleton
            planSlugs={skeletonPlanSlugs}
            singlePlan={skeletonSinglePlan}
          />
        ),
      };
    }
    if (
      screen === "unpriced" ||
      pricingDto.pricing.status !== "ok" ||
      pricingDto.pricing.plans.length === 0
    ) {
      return { key: "unpriced", content: <PricingUnavailableView /> };
    }
    const shownPlans = visiblePlans(pricingDto.pricing.plans, flags);
    return {
      key: "quote",
      content: (
        <QuoteScreen
          quoteType={quoteSubjectType}
          plans={shownPlans}
          includedInAllFeatures={hiddenBaselineFeatures(
            pricingDto.pricing.plans,
            shownPlans,
          )}
          planningFor={pricingDto.planning_for}
          insuredFirstName={pricingDto.insured_first_name}
          insuredFullName={pricingDto.insured_full_name}
          insuredAge={pricingDto.insured_age}
          insuredZip={pricingDto.insured_zip}
          marketSlug={pricingDto.market_slug}
          preparedAt={preparedAtOf(pricingDto)}
          ownerFirstName={pricingDto.owner_first_name}
          initialSelection={view ? view.current_selection : null}
          defaultSelection={flags.default_selection}
          singlePlan={singlePlanTreatment(shownPlans)}
          bookedCall={view ? bookedCall : null}
          contactPhone={view ? displayPhone(view.contact_phone) : null}
          onScheduleCall={() => requestSchedule("phone_band")}
          busy={continuing}
          error={continueError}
          onContinue={requestContinue}
        />
      ),
    };
  };

  const { key, content } = phase();

  // The pixel fires once per quote per device on the quote screen's first
  // render — bootstrap cards count exactly the same as a real view. The
  // provider's own dedupe (keyed on quoteId in localStorage) makes this
  // effect idempotent across re-renders and reloads alike.
  useEffect(() => {
    if (key !== "quote" || !quoteId) return;
    trackEvent({
      event: "PreNeedPricingViewAQuote",
      eventId: quoteId,
      data: {
        content_name: "pre-need-quote",
        content_category: "pre-need",
        lead_interest: "pre-need",
      },
    });
  }, [key, quoteId, trackEvent]);

  // One `quote_flow_screen_viewed` per phase change — never on a
  // re-render that leaves `key` untouched. The skeleton and the arrival
  // wait are chrome, not screens the family actually reads, so they are
  // the phases excluded.
  const reportedScreenRef = useRef<string | null>(null);
  useEffect(() => {
    if (key === "skeleton" || key === "arrival-loading") return;
    // StrictMode runs a mount effect twice with the same key; a family
    // saw one screen, not two.
    if (reportedScreenRef.current === key) return;
    reportedScreenRef.current = key;
    const props: AnalyticsProperties = { screen: SCREEN_NAMES[key] ?? key };
    if (key === "quote" && pricingDto && flags) {
      const shown = visiblePlans(pricingDto.pricing.plans, flags);
      props.source = view ? "view" : "bootstrap";
      props.plans_shown = shown.map((plan) => plan.slug);
      props.single_plan_treatment = singlePlanTreatment(shown);
      props.default_plan_slug = flags.default_selection?.plan_slug ?? null;
      props.default_option_slug = flags.default_selection?.option_slug ?? null;
    }
    if (key === "interstitial" && credential.kind === "refused") {
      props.reason = credential.reason;
    }
    // The dead ends say what led there, so "dead ends by cause" is one
    // query on this event rather than a join against request_failed.
    if (key === "not-found" || key === "error") {
      props.operation = fatal?.operation ?? null;
      props.status = fatal?.status ?? null;
    }
    if (key === "exhausted") {
      props.operation = "mint_session_poll";
      props.status = null;
    }
    if (key === "unpriced" && pricingDto) {
      props.reason =
        pricingDto.pricing.status !== "ok"
          ? pricingDto.pricing.status
          : "no_plans";
    }
    track("quote_flow_screen_viewed", props);
    // Deliberately keyed on `key` alone — the other values read inside
    // are a snapshot of the phase actually being reported, not something
    // that should re-fire the event when they change out from under it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const showNavActions =
    key !== "not-found" &&
    key !== "error" &&
    key !== "interstitial" &&
    key !== "exhausted" &&
    pricingDto !== null;

  return (
    <div className="flex min-h-screen flex-col overflow-x-clip bg-[#FBF7EC]">
      <Header
        quoteFlow
        // The bar's width follows the content under it: the application's
        // current column (its juncture faces widen to the card grid),
        // or the wide grid everywhere else.
        quoteFlowWidth={flowBarWidth({
          screen: key,
          station: step,
          requested,
          applicationWidth,
        })}
        // The call cluster reveals only once pricing actually exists —
        // bootstrap cards count — and never on a dead end that has
        // nothing to schedule against.
        actions={
          showNavActions ? (
            <NavCallActions onSchedule={() => requestSchedule("nav")} />
          ) : null
        }
      />
      {quoteId &&
      saveQuoteBarVisible({
        station: step,
        originKind: view?.quote.origin_kind ?? null,
        signedIn,
        userLoading,
        dismissed: barDismissed,
      }) ? (
        <SaveQuoteBar
          quoteId={quoteId}
          onDismiss={() => {
            writeBarDismissed(quoteId);
            setBarDismissed(true);
          }}
        />
      ) : null}
      <PhaseContainer key={key} phaseKey={key}>
        {content}
      </PhaseContainer>
      {callModalOpen && quoteId ? (
        <ScheduleCallModal
          quoteId={quoteId}
          credentialPending={credential.kind !== "granted"}
          contactPhone={displayPhone(view?.contact_phone ?? null)}
          existingCall={bookedCall}
          // Only the quote screen has an application to move into;
          // anywhere else the confirmation's Continue just closes — a
          // booked call is the opposite of a dismissal, so it must not
          // fall back to onClose and its dismissed event.
          onContinueToApplication={
            key === "quote" ? enterApplication : () => setCallModalOpen(false)
          }
          onBooked={setBookedCall}
          onClose={() => {
            track("quote_flow_schedule_dismissed");
            setCallModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The default export: owns only what the analytics provider needs above
 * every tracked effect and handler in `QuoteFlowContent` — the query
 * string's one-time read and the static credential channel it implies
 * (the step reaches the scope through `setStep`, from the flow that
 * derives it off the URL).
 */
export default function QuoteFlow() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const posthog = usePostHog();

  // Leaving the flow drops the PostHog `quote` group flags.ts registered,
  // so nothing captured elsewhere in the app rides under this quote. The
  // reset is deferred a tick and cancelled by a remount: React StrictMode
  // (dev) runs mount → cleanup → mount synchronously on the same
  // instance, and a reset in between would strip the group flags.ts
  // won't re-register (its guard ref survives the double-invoke).
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    return () => {
      leaveTimerRef.current = setTimeout(() => {
        leaveTimerRef.current = null;
        posthog.resetGroups();
      }, 0);
    };
  }, [posthog]);

  // Read once at mount — the credential rides the URL exactly once; the
  // link path clears it from the bar immediately, and the signed-handoff
  // path leaves it (it's the shareable read-only link).
  const [query] = useState<ArrivalQuery>(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      t: params.get("t"),
      ped: params.get("ped"),
      sig: params.get("sig"),
      exp: params.get("exp"),
    };
  });
  const credentialChannel: InterstitialCredential = query.t
    ? "link"
    : query.ped && query.sig && query.exp
      ? "signature"
      : "none";

  return (
    <QuoteFlowAnalyticsProvider
      quoteId={quoteId ?? ""}
      quoteType="policy"
      credential={credentialChannel}
    >
      <QuoteFlowContent quoteId={quoteId} query={query} />
    </QuoteFlowAnalyticsProvider>
  );
}

/** The terminal informational faces fade up on arrival; everything else
 * swaps instantly — the skeleton IS the quote screen's transition, and
 * animating the reveal on top of it would defeat the point. */
const FADE_UP_PHASES = new Set([
  "not-found",
  "error",
  "unpriced",
  "done",
  "interstitial",
  "exhausted",
]);

function PhaseContainer({
  phaseKey,
  children,
}: {
  phaseKey: string;
  children: ReactNode;
}) {
  if (!FADE_UP_PHASES.has(phaseKey)) {
    return <div className="flex flex-1 flex-col">{children}</div>;
  }
  return (
    <motion.div
      className="flex flex-1 flex-col"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}
